import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Check, Star, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import logoIcon from '@/assets/logo-icon.webp';
import { isValidGoogleUrl } from '@/lib/urlValidation';

interface Business {
  id: string;
  name: string;
  industry: string;
  location: string;
  service_area: string;
  google_profile_url: string | null;
  logo_url: string | null;
}

interface Category {
  id: string;
  name: string;
  description: string | null;
}

interface Review {
  id: string;
  review_text: string;
  keywords: string[] | null;
  category_id: string | null;
  category_name: string;
}

export default function CustomerReviewPage() {
  const { businessId } = useParams<{ businessId: string }>();
  const [business, setBusiness] = useState<Business | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  useEffect(() => {
    if (businessId) {
      fetchBusinessData();
    }
  }, [businessId]);

  const fetchBusinessData = async () => {
    try {
      // Fetch business from public view (excludes sensitive user_id field)
      const { data: businessData, error: businessError } = await supabase
        .from('businesses_public' as any)
        .select('id, name, industry, category, location, service_area, services, logo_url, google_profile_url')
        .eq('id', businessId)
        .maybeSingle() as { data: { id: string; name: string; industry: string; category: string; location: string; service_area: string; services: string[] | null; logo_url: string | null; google_profile_url: string | null } | null; error: any };

      if (businessError) throw businessError;
      if (!businessData) {
        setLoading(false);
        return;
      }
      setBusiness({ 
        id: businessData.id,
        name: businessData.name,
        industry: businessData.industry,
        location: businessData.location,
        service_area: businessData.service_area,
        google_profile_url: businessData.google_profile_url,
        logo_url: businessData.logo_url
      });

      // Fetch categories from public view
      const { data: categoriesData, error: categoriesError } = await supabase
        .from('review_categories_public' as any)
        .select('id, name, description')
        .eq('business_id', businessId) as { data: Category[] | null; error: any };

      if (categoriesError) throw categoriesError;
      setCategories(categoriesData || []);

      // Fetch pre-generated reviews that haven't been copied yet
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('generated_reviews')
        .select('id, review_text, keywords, category_id, category_name')
        .eq('business_id', businessId)
        .eq('copied', false)
        .order('created_at', { ascending: false });

      if (reviewsError) throw reviewsError;
      setReviews(reviewsData || []);
    } catch (err) {
      console.error('Error fetching data:', err);
      toast.error('Unable to load business information');
    } finally {
      setLoading(false);
    }
  };

  const handleCopyAndSubmit = async (review: Review) => {
    navigator.clipboard.writeText(review.review_text);
    setCopiedId(review.id);
    toast.success('Review copied! Opening Google...');

    // Remove the copied review from local state immediately to prevent duplicates
    setReviews(prev => prev.filter(r => r.id !== review.id));

    // Mark review as copied and submitted in database
    const { error } = await supabase
      .from('generated_reviews')
      .update({ copied: true, submitted: true })
      .eq('id', review.id);
    
    if (error) {
      console.error('Error marking review as copied/submitted:', error);
    }
    
    setTimeout(() => {
      setCopiedId(null);
      if (business?.google_profile_url && isValidGoogleUrl(business.google_profile_url)) {
        // Use same window navigation for better Safari/mobile compatibility
        window.location.href = business.google_profile_url;
      } else if (business?.google_profile_url) {
        toast.error('Invalid Google Business Profile URL configured.');
      } else {
        toast.info('Google Business Profile link not available. Please paste the review manually.');
      }
    }, 1500);
  };

  const filteredReviews = activeCategory === 'all' 
    ? reviews 
    : reviews.filter(r => r.category_id === activeCategory || r.category_name === categories.find(c => c.id === activeCategory)?.name);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4">
          <div className="w-16 h-16 mx-auto rounded-full bg-primary/20 flex items-center justify-center animate-pulse-soft">
            <Star className="w-8 h-8 text-primary" />
          </div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4 max-w-sm">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-destructive/10 flex items-center justify-center">
            <span className="text-3xl">😔</span>
          </div>
          <h1 className="text-xl font-semibold">Business Not Found</h1>
          <p className="text-muted-foreground text-sm">
            The review link you followed may be invalid or expired.
          </p>
        </div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="text-center space-y-4 max-w-sm">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-muted flex items-center justify-center">
            <Star className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="text-xl font-semibold">No Reviews Available</h1>
          <p className="text-muted-foreground text-sm">
            {business.name} hasn't prepared any review suggestions yet. Please check back later!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background safe-bottom">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="page-container py-4">
          <div className="flex items-center justify-center gap-3">
            {business.logo_url ? (
              <img src={business.logo_url} alt={business.name} className="w-12 h-12 rounded-xl object-contain" loading="lazy" />
            ) : (
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <span className="text-xl font-bold text-primary">{business.name.charAt(0)}</span>
              </div>
            )}
            <div className="text-center">
              <h1 className="text-lg font-semibold">{business.name}</h1>
              <p className="text-xs text-muted-foreground mt-0.5">
                Share your experience with us
              </p>
            </div>
          </div>
        </div>
      </header>

      <main className="page-container pb-8">
        {/* Stars illustration */}
        <div className="flex justify-center gap-1 my-6">
          {[1, 2, 3, 4, 5].map((star) => (
            <Star
              key={star}
              className="w-8 h-8 fill-warning text-warning animate-fade-in"
              style={{ animationDelay: `${star * 100}ms` }}
            />
          ))}
        </div>

        {/* Intro text */}
        <div className="text-center mb-6 animate-slide-up">
          <h2 className="text-lg font-semibold mb-2">Thank you for choosing us!</h2>
          <p className="text-sm text-muted-foreground">
            Pick a review that best describes your experience, copy it, and share it on Google.
          </p>
        </div>

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="mb-6">
            <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-2">
              <button
                onClick={() => setActiveCategory('all')}
                className={`tab-button ${
                  activeCategory === 'all'
                    ? 'tab-button-active'
                    : 'tab-button-inactive'
                }`}
              >
                All Reviews
              </button>
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setActiveCategory(category.id)}
                  className={`tab-button ${
                    activeCategory === category.id
                      ? 'tab-button-active'
                      : 'tab-button-inactive'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Reviews List */}
        <div className="space-y-4">
          {filteredReviews.map((review, index) => (
            <div
              key={review.id}
              className="review-card animate-fade-in"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              {/* Category badge */}
              <div className="mb-2">
                <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-medium">
                  {review.category_name}
                </span>
              </div>
              
              <p className="text-sm leading-relaxed mb-4">{review.review_text}</p>
              
              <Button
                onClick={() => handleCopyAndSubmit(review)}
                className="gradient-btn w-full h-12 rounded-xl"
                disabled={copiedId === review.id}
              >
                {copiedId === review.id ? (
                  <>
                    <Check className="mr-2 text-success" size={18} />
                    Copied! Opening Google...
                  </>
                ) : (
                  <>
                    <ExternalLink className="mr-2" size={18} />
                    Copy & Submit Review
                  </>
                )}
              </Button>
            </div>
          ))}
        </div>

        {/* Instructions */}
        <div className="soft-card bg-accent/50 mt-6 animate-slide-up">
          <h3 className="font-medium mb-2 text-sm">How to post your review:</h3>
          <ol className="text-sm text-muted-foreground space-y-1.5 list-decimal list-inside">
            <li>Choose a review that matches your experience</li>
            <li>Click "Copy & Submit Review"</li>
            <li>You'll be redirected to Google Maps</li>
            <li>Find "Write a review" and paste your copied text</li>
            <li>Feel free to personalize it before submitting!</li>
          </ol>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border py-6">
        <div className="page-container">
          <div className="flex items-center justify-center gap-2">
            <img src={logoIcon} alt="Review Plum" className="w-5 h-5" loading="lazy" />
            <p className="text-xs text-muted-foreground">
              Powered by Review Plum
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}